<template>
    <div>
        <v-card>
            <v-card-title class="headline">
                <v-btn icon @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <span>Edit Invoice</span>
            </v-card-title>
            <v-card-text>
                <v-form v-model="valid">
                    <v-row v-if="canEditInvoice">
                        <v-col cols="12" md="12">
                            <v-menu
                                v-model="invoiceDateMenu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="localInvoice.invoiceDate"
                                        label="Invoice Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="localInvoice.invoiceDate"
                                    @input="invoiceDateMenu = false"
                                    scrollable
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-row v-if="canEditInvoice">
                        <v-col cols="12">
                            <v-menu
                                v-model="dateMenu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="localInvoice.dueDate"
                                        label="Due Date"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="localInvoice.dueDate"
                                    @input="dateMenu = false"
                                    scrollable
                                />
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-row v-if="canMarkCreditNote">
                        <v-col cols="12">
                            <v-switch
                                :input-value="!!localInvoice.creditNote"
                                label="Credit note"
                                color="error"
                                hide-details
                                class="mt-0"
                                @change="onCreditNoteChange"
                            />
                            <p
                                v-if="localInvoice.creditNote"
                                class="mb-0 mt-2"
                                style="color: #f44336; font-weight: 600;"
                            >
                                {{ localInvoice.consecutive }}
                            </p>
                        </v-col>
                    </v-row>
                    <v-row v-if="noOrderFiles.length > 0">
                        <v-col cols="12">
                            <div class="text-body-2 mb-2">
                                Current files:
                            </div>
                            <div
                                v-for="(file, index) in noOrderFiles"
                                :key="index"
                                class="d-flex align-center mb-2"
                            >
                                <v-text-field
                                    v-if="
                                        canEditInvoice &&
                                            noOrderFileOrigins[index]
                                    "
                                    v-model="noOrderFiles[index]"
                                    dense
                                    hide-details
                                    class="mr-2"
                                    style="max-width: 360px;"
                                />
                                <a
                                    v-else
                                    href="#"
                                    @click.prevent="
                                        openNoOrderFile(
                                            noOrderFileOrigins[index] || file
                                        )
                                    "
                                    class="text-body-2 text-decoration-none"
                                    style="color: #1976d2; cursor: pointer;"
                                >
                                    {{ file }}
                                </a>
                                <v-btn
                                    v-if="canRejectInvoice"
                                    icon
                                    x-small
                                    color="error"
                                    class="ml-2"
                                    @click="confirmDeleteNoOrderFile(file)"
                                    :loading="deletingFile === file"
                                >
                                    <v-icon x-small>mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="canRejectInvoice">
                        <v-col cols="12">
                            <v-btn
                                @click="$refs.noOrderFileInput.click()"
                                color="primary"
                                outlined
                                prepend-icon="mdi-file-upload"
                                block
                                :loading="addingFile"
                            >
                                Add Invoice File
                            </v-btn>
                            <input
                                ref="noOrderFileInput"
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                multiple
                                @change="handleNoOrderFileSelection"
                                style="display: none"
                            />
                        </v-col>
                    </v-row>

                    <!-- Invoice Attachments Section -->
                    <v-divider class="my-4"></v-divider>
                    <v-row>
                        <v-col cols="12">
                            <div class="text-body-1 font-weight-medium mb-2">
                                Invoice Attachments
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="invoiceAttachments.length > 0">
                        <v-col cols="12">
                            <div class="text-body-2 mb-2">
                                Current attachments:
                            </div>
                            <div
                                v-for="(attachment,
                                index) in invoiceAttachments"
                                :key="index"
                                class="d-flex align-center mb-2"
                            >
                                <v-text-field
                                    v-if="
                                        canEditInvoice &&
                                            attachmentOrigins[index]
                                    "
                                    v-model="invoiceAttachments[index]"
                                    dense
                                    hide-details
                                    class="mr-2"
                                    style="max-width: 360px;"
                                />
                                <a
                                    v-else
                                    href="#"
                                    @click.prevent="
                                        openAttachment(
                                            attachmentOrigins[index] ||
                                                attachment
                                        )
                                    "
                                    class="text-body-2 text-decoration-none"
                                    style="color: #1976d2; cursor: pointer;"
                                >
                                    {{ attachment }}
                                </a>
                                <v-btn
                                    v-if="canRejectInvoice"
                                    icon
                                    x-small
                                    color="error"
                                    class="ml-2"
                                    @click="confirmDeleteAttachment(attachment)"
                                    :loading="deletingAttachment === attachment"
                                >
                                    <v-icon x-small>mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="canRejectInvoice">
                        <v-col cols="12">
                            <v-btn
                                @click="$refs.attachmentInput.click()"
                                color="primary"
                                outlined
                                prepend-icon="mdi-paperclip"
                                block
                                :loading="addingAttachment"
                            >
                                Attach
                            </v-btn>
                            <input
                                ref="attachmentInput"
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                multiple
                                @change="handleAttachmentSelection"
                                style="display: none"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-card-actions class="mt-3">
                <v-btn
                    v-if="canRejectInvoice && !localInvoice.rejected"
                    text
                    color="error"
                    @click="rejectDialog = true"
                    :disabled="loading"
                >
                    Reject
                </v-btn>
                <v-btn
                    v-if="canRejectInvoice && localInvoice.rejected"
                    text
                    color="error"
                    @click="cancelRejectionDialog = true"
                    :disabled="loading"
                >
                    Cancel Rejection
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    color="primary"
                    @click="updatePurchaseInvoice"
                    :loading="loading"
                    :disabled="!canSave"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Reject Dialog -->
        <v-dialog v-model="rejectDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title>
                    Reject Invoice
                </v-card-title>
                <v-card-text>
                    <p>Are you sure you want to reject this invoice?</p>
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="rejectDialog = false">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="error" @click="reject" :disabled="loading">
                        Reject
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Cancel Rejection Dialog -->
        <v-dialog v-model="cancelRejectionDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title>
                    Cancel Rejection
                </v-card-title>
                <v-card-text>
                    <p>
                        Are you sure you want to cancel the rejection of this
                        invoice?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        color="secondary"
                        @click="cancelRejectionDialog = false"
                    >
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="error"
                        @click="cancelRejection"
                        :disabled="loading"
                    >
                        Cancel
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete File Confirmation Dialog -->
        <v-dialog v-model="deleteFileDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title>
                    Delete File
                </v-card-title>
                <v-card-text>
                    <p>
                        Are you sure you want to delete the file
                        <strong>{{ fileToDelete }}</strong
                        >?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        color="secondary"
                        @click="deleteFileDialog = false"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="error"
                        @click="deleteNoOrderFile"
                        :loading="deletingFile === fileToDelete"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Attachment Confirmation Dialog -->
        <v-dialog v-model="deleteAttachmentDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title>
                    Delete Attachment
                </v-card-title>
                <v-card-text>
                    <p>
                        Are you sure you want to delete the attachment
                        <strong>{{ attachmentToDelete }}</strong
                        >?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        color="secondary"
                        @click="deleteAttachmentDialog = false"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="error"
                        @click="deleteAttachment"
                        :loading="deletingAttachment === attachmentToDelete"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import _ from 'lodash'
import API from '@/services/api'
import { mapMutations } from 'vuex'
import { saveFile } from '@/services/storage/saveFile'
import { deleteFile } from '@/services/storage/deleteFile'
import { openFile } from '@/services/storage/openFile'
import { renameFile } from '@/services/storage/renameFile'
import { withCreditNotePrefix } from '@/components/accountant/creditNote'

export default {
    name: 'EditNoOrderInvoice',
    props: {
        invoice: {
            type: Object,
            required: true,
        },
        purchase: {
            type: Object,
            required: true,
        },
        canEditInvoice: {
            type: Boolean,
            default: false,
        },
        canRejectInvoice: {
            type: Boolean,
            default: false,
        },
        canMarkCreditNote: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            valid: false,
            loading: false,
            dateMenu: false,
            invoiceDateMenu: false,
            rejectDialog: false,
            localInvoice: { dueDate: '', invoiceDate: '', consecutive: '' },
            companyId: JSON.parse(localStorage.getItem('company')),
            cancelRejectionDialog: false,
            noOrderFiles: [],
            originalNoOrderFiles: [],
            noOrderFileOrigins: [],
            deleteFileDialog: false,
            fileToDelete: null,
            deletingFile: null,
            addingFile: false,
            filesToAdd: [],
            filesToRemove: [],
            pendingFilesToUpload: [],
            // Invoice Attachments management
            invoiceAttachments: [],
            originalInvoiceAttachments: [],
            attachmentOrigins: [],
            deleteAttachmentDialog: false,
            attachmentToDelete: null,
            deletingAttachment: null,
            addingAttachment: false,
            attachmentsToAdd: [],
            attachmentsToRemove: [],
            pendingAttachmentsToUpload: [],
        }
    },
    watch: {
        invoice: {
            handler(newVal) {
                if (newVal) {
                    this.localInvoice = _.cloneDeep(newVal)
                    this.$nextTick(() => {
                        this.loadInvoiceAttachments()
                    })
                }
            },
            immediate: true,
            deep: true,
        },
        purchase: {
            handler(newVal) {
                if (newVal) {
                    this.$nextTick(() => {
                        this.loadNoOrderFiles()
                    })
                }
            },
            immediate: true,
            deep: true,
        },
    },
    mounted() {
        this.localInvoice = _.cloneDeep(this.invoice)
        // Use double nextTick to ensure purchase prop is fully processed
        this.$nextTick(() => {
            this.$nextTick(() => {
                this.loadNoOrderFiles()
                this.loadInvoiceAttachments()
            })
        })
    },
    computed: {
        canSave() {
            if (this.loading) return false
            if (this.canEditInvoice || this.canMarkCreditNote) return true
            return !!(
                this.canRejectInvoice &&
                (this.filesToAdd.length ||
                    this.filesToRemove.length ||
                    this.attachmentsToAdd.length ||
                    this.attachmentsToRemove.length)
            )
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        onCreditNoteChange(enabled) {
            const currentFile =
                (this.noOrderFileOrigins && this.noOrderFileOrigins[0]) ||
                (this.noOrderFiles && this.noOrderFiles[0]) ||
                ''
            if (enabled) {
                const previous = this.localInvoice.consecutive || null
                this.$set(
                    this.localInvoice,
                    'creditNotePreviousConsecutive',
                    previous
                )
                const base =
                    (previous && String(previous).trim()) || currentFile
                this.$set(
                    this.localInvoice,
                    'consecutive',
                    withCreditNotePrefix(base)
                )
                this.$set(this.localInvoice, 'creditNote', true)
                return
            }
            const restored =
                this.localInvoice.creditNotePreviousConsecutive !== undefined
                    ? this.localInvoice.creditNotePreviousConsecutive
                    : null
            this.$set(this.localInvoice, 'consecutive', restored || null)
            this.$set(this.localInvoice, 'creditNote', false)
        },
        async openNoOrderFile(fileName) {
            const path = `${this.companyId}/noOrderPurchases/${this.purchase.id}/files`
            await openFile(fileName, path)
        },
        async reject() {
            try {
                this.loading = true
                await API.rejectNoOrderPurchase({
                    companyId: this.companyId,
                    documentId: this.purchase.id,
                    cancelRejection: false,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeDialog()
            }
        },
        async cancelRejection() {
            try {
                this.loading = true
                await API.rejectNoOrderPurchase({
                    companyId: this.companyId,
                    documentId: this.purchase.id,
                    cancelRejection: true,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeDialog()
            }
        },
        closeDialog() {
            // Reset pending files when closing without saving
            this.pendingFilesToUpload = []
            this.filesToAdd = []
            this.filesToRemove = []
            // Restore original files list
            this.noOrderFiles = [...this.originalNoOrderFiles]
            // Reset pending attachments when closing without saving
            this.pendingAttachmentsToUpload = []
            this.attachmentsToAdd = []
            this.attachmentsToRemove = []
            // Restore original attachments list
            this.invoiceAttachments = [...this.originalInvoiceAttachments]
            this.$emit('closeDialog')
        },
        loadInvoiceAttachments() {
            // Load attachments from the invoice (invoice.invoiceAttachments)
            this.invoiceAttachments = []
            this.originalInvoiceAttachments = []

            // Try to get invoiceAttachments from localInvoice first, then from invoice prop
            let attachments = null
            if (this.localInvoice?.invoiceAttachments) {
                attachments = this.localInvoice.invoiceAttachments
            } else if (this.invoice?.invoiceAttachments) {
                attachments = this.invoice.invoiceAttachments
                // Update localInvoice with attachments from invoice prop
                if (!this.localInvoice) {
                    this.localInvoice = {}
                }
                this.localInvoice.invoiceAttachments = attachments
            }

            if (!attachments) {
                return
            }

            if (Array.isArray(attachments) && attachments.length > 0) {
                this.invoiceAttachments = [...attachments]
                this.originalInvoiceAttachments = [...attachments]
                this.attachmentOrigins = attachments.map(name => name)
            } else {
                this.attachmentOrigins = []
            }
        },
        async openAttachment(fileName) {
            const path = `${this.companyId}/noOrderPurchases/${this.purchase.id}/invoiceAttatchments`
            await openFile(fileName, path)
        },
        handleAttachmentSelection(event) {
            const files = Array.from(event.target.files)
            if (files.length > 0) {
                this.addAttachments(files)
            }
            if (this.$refs.attachmentInput) {
                this.$refs.attachmentInput.value = ''
            }
        },
        async addAttachments(files) {
            try {
                this.addingAttachment = true

                // Ensure we have the current list of attachments
                if (this.invoiceAttachments.length === 0) {
                    this.loadInvoiceAttachments()
                }

                const usedAttachmentNames = new Set([
                    ...this.invoiceAttachments,
                    ...this.pendingAttachmentsToUpload.map(f => f.name),
                ])
                const renamedFiles = files.map(file => {
                    const newFileName = this.uniqueFileName(
                        file.name,
                        usedAttachmentNames
                    )
                    return new File([file], newFileName, {
                        type: file.type,
                        lastModified: file.lastModified,
                    })
                })

                const newFileNames = renamedFiles.map(file => file.name)

                // Store renamed File objects temporarily (don't upload until Save)
                this.pendingAttachmentsToUpload = [
                    ...this.pendingAttachmentsToUpload,
                    ...renamedFiles,
                ]

                // Update local list for display
                this.invoiceAttachments = [
                    ...this.invoiceAttachments,
                    ...newFileNames,
                ]
                this.attachmentOrigins = [
                    ...this.attachmentOrigins,
                    ...newFileNames.map(() => null),
                ]

                // Add to attachmentsToAdd array for later confirmation
                this.attachmentsToAdd = [
                    ...this.attachmentsToAdd,
                    ...newFileNames,
                ]
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.addingAttachment = false
            }
        },
        confirmDeleteAttachment(fileName) {
            this.attachmentToDelete = fileName
            this.deleteAttachmentDialog = true
        },
        async deleteAttachment() {
            if (!this.attachmentToDelete) return

            try {
                this.deletingAttachment = this.attachmentToDelete

                const attachmentIndex = this.invoiceAttachments.indexOf(
                    this.attachmentToDelete
                )
                const origin =
                    attachmentIndex >= 0
                        ? this.attachmentOrigins[attachmentIndex]
                        : null
                if (attachmentIndex >= 0) {
                    this.invoiceAttachments.splice(attachmentIndex, 1)
                    this.attachmentOrigins.splice(attachmentIndex, 1)
                }

                const attachmentsToAddIndex = this.attachmentsToAdd.indexOf(
                    origin == null ? this.attachmentToDelete : origin
                )
                if (attachmentsToAddIndex !== -1) {
                    // Attachment was newly added, just remove from attachmentsToAdd and pendingAttachmentsToUpload
                    this.attachmentsToAdd.splice(attachmentsToAddIndex, 1)

                    // Also remove from pendingAttachmentsToUpload (File objects)
                    this.pendingAttachmentsToUpload = this.pendingAttachmentsToUpload.filter(
                        file => file.name !== this.attachmentToDelete
                    )
                } else if (
                    origin &&
                    !this.attachmentsToRemove.includes(origin)
                ) {
                    this.attachmentsToRemove.push(origin)
                }

                this.deleteAttachmentDialog = false
                this.attachmentToDelete = null
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.deletingAttachment = null
            }
        },
        loadNoOrderFiles() {
            // Load files from purchase
            this.noOrderFiles = []
            this.originalNoOrderFiles = []
            this.noOrderFileOrigins = []

            if (!this.purchase) {
                return
            }

            // Use only file
            // file can be array or single string
            if (this.purchase.file) {
                if (
                    Array.isArray(this.purchase.file) &&
                    this.purchase.file.length > 0
                ) {
                    this.noOrderFiles = [...this.purchase.file]
                    this.originalNoOrderFiles = [...this.purchase.file]
                    this.noOrderFileOrigins = this.purchase.file.map(
                        name => name
                    )
                } else if (
                    typeof this.purchase.file === 'string' &&
                    this.purchase.file.trim() !== ''
                ) {
                    this.noOrderFiles = [this.purchase.file]
                    this.originalNoOrderFiles = [this.purchase.file]
                    this.noOrderFileOrigins = [this.purchase.file]
                }
            }
        },
        handleNoOrderFileSelection(event) {
            const files = Array.from(event.target.files)
            if (files.length > 0) {
                this.addNoOrderFiles(files)
            }
            if (this.$refs.noOrderFileInput) {
                this.$refs.noOrderFileInput.value = ''
            }
        },
        /**
         * Storage-safe unique names in the same folder; registers in `usedNames`.
         */
        uniqueFileName(originalName, usedNames) {
            const lastDot = originalName.lastIndexOf('.')
            const extension =
                lastDot !== -1 ? originalName.substring(lastDot) : ''
            const base =
                lastDot !== -1
                    ? originalName.substring(0, lastDot)
                    : originalName

            for (let n = 0; n < 10000; n += 1) {
                const candidate =
                    n === 0
                        ? `${base}${extension}`
                        : `${base} (${n})${extension}`
                if (!usedNames.has(candidate)) {
                    usedNames.add(candidate)
                    return candidate
                }
            }
            const fallback = `${base} (${Date.now()})${extension}`
            usedNames.add(fallback)
            return fallback
        },
        async addNoOrderFiles(files) {
            try {
                this.addingFile = true

                // Ensure we have the current list of files
                // Reload if the list is empty but purchase has files
                if (this.noOrderFiles.length === 0) {
                    this.loadNoOrderFiles()
                }

                const usedNoOrderNames = new Set([
                    ...this.noOrderFiles,
                    ...this.pendingFilesToUpload.map(f => f.name),
                ])
                const renamedFiles = files.map(file => {
                    const newFileName = this.uniqueFileName(
                        file.name,
                        usedNoOrderNames
                    )
                    return new File([file], newFileName, {
                        type: file.type,
                        lastModified: file.lastModified,
                    })
                })

                const newFileNames = renamedFiles.map(file => file.name)

                // Store renamed File objects temporarily (don't upload until Save)
                this.pendingFilesToUpload = [
                    ...this.pendingFilesToUpload,
                    ...renamedFiles,
                ]

                // Update local list for display - preserve existing files and add new ones
                this.noOrderFiles = [...this.noOrderFiles, ...newFileNames]
                this.noOrderFileOrigins = [
                    ...this.noOrderFileOrigins,
                    ...newFileNames.map(() => null),
                ]

                // Add to filesToAdd array for later confirmation
                this.filesToAdd = [...this.filesToAdd, ...newFileNames]
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.addingFile = false
            }
        },
        confirmDeleteNoOrderFile(fileName) {
            this.fileToDelete = fileName
            this.deleteFileDialog = true
        },
        async deleteNoOrderFile() {
            if (!this.fileToDelete) return

            try {
                this.deletingFile = this.fileToDelete

                const fileIndex = this.noOrderFiles.indexOf(this.fileToDelete)
                const origin =
                    fileIndex >= 0 ? this.noOrderFileOrigins[fileIndex] : null
                if (fileIndex >= 0) {
                    this.noOrderFiles.splice(fileIndex, 1)
                    this.noOrderFileOrigins.splice(fileIndex, 1)
                }

                const filesToAddIndex = this.filesToAdd.indexOf(
                    origin == null ? this.fileToDelete : origin
                )
                if (filesToAddIndex !== -1) {
                    this.filesToAdd.splice(filesToAddIndex, 1)
                    this.pendingFilesToUpload = this.pendingFilesToUpload.filter(
                        file => file.name !== this.fileToDelete
                    )
                } else if (origin && !this.filesToRemove.includes(origin)) {
                    this.filesToRemove.push(origin)
                }

                this.deleteFileDialog = false
                this.fileToDelete = null
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.deletingFile = null
            }
        },
        collectRenames(names, origins) {
            const renames = []
            names.forEach((name, index) => {
                const origin = origins[index]
                const nextName = (name || '').trim()
                if (origin && nextName && origin !== nextName) {
                    renames.push({ from: origin, to: nextName })
                }
            })
            return renames
        },
        async applyStorageRenames(renames) {
            const applied = []
            try {
                for (const rename of renames) {
                    await renameFile(rename.from, rename.to, rename.path)
                    applied.push(rename)
                }
            } catch (error) {
                for (const rename of applied.reverse()) {
                    try {
                        await renameFile(rename.to, rename.from, rename.path)
                    } catch (rollbackError) {
                        console.error(
                            `Error restoring ${rename.to}:`,
                            rollbackError
                        )
                    }
                }
                throw error
            }
            return applied
        },
        async updatePurchaseInvoice() {
            try {
                this.loading = true

                this.noOrderFiles = this.noOrderFiles
                    .map(name => (name || '').trim())
                    .filter(Boolean)
                this.invoiceAttachments = this.invoiceAttachments
                    .map(name => (name || '').trim())
                    .filter(Boolean)

                const finalFiles = [...new Set(this.noOrderFiles)]
                const finalAttachments = [...new Set(this.invoiceAttachments)]
                const fileRenames = this.canEditInvoice
                    ? this.collectRenames(
                          this.noOrderFiles,
                          this.noOrderFileOrigins
                      )
                    : []
                const attachmentRenames = this.canEditInvoice
                    ? this.collectRenames(
                          this.invoiceAttachments,
                          this.attachmentOrigins
                      )
                    : []
                this.localInvoice.invoiceAttachments = finalAttachments

                const path = `${this.companyId}/noOrderPurchases/${this.purchase.id}/files`
                const attachmentsPath = `${this.companyId}/noOrderPurchases/${this.purchase.id}/invoiceAttatchments`
                const storedRenames = [
                    ...fileRenames
                        .filter(
                            rename => !this.filesToRemove.includes(rename.from)
                        )
                        .map(rename => ({ ...rename, path })),
                    ...attachmentRenames
                        .filter(
                            rename =>
                                !this.attachmentsToRemove.includes(rename.from)
                        )
                        .map(rename => ({ ...rename, path: attachmentsPath })),
                ]
                const appliedRenames = await this.applyStorageRenames(
                    storedRenames
                )

                try {
                    await API.updateNoOrderPurchase({
                        companyId: this.companyId,
                        documentId: this.purchase.id,
                        dueDate: this.canEditInvoice
                            ? this.localInvoice.dueDate || null
                            : undefined,
                        invoiceDate: this.canEditInvoice
                            ? this.localInvoice.invoiceDate || null
                            : undefined,
                        consecutive: this.canMarkCreditNote
                            ? this.localInvoice.consecutive || null
                            : undefined,
                        creditNote: this.canMarkCreditNote
                            ? !!this.localInvoice.creditNote
                            : undefined,
                        file: finalFiles,
                        invoiceAttachments: finalAttachments,
                        fileRenames,
                        attachmentRenames,
                    })
                } catch (error) {
                    await this.applyStorageRenames(
                        appliedRenames
                            .slice()
                            .reverse()
                            .map(rename => ({
                                from: rename.to,
                                to: rename.from,
                                path: rename.path,
                            }))
                    )
                    throw error
                }

                // Delete removed files BEFORE uploading new ones.
                // If the same filename is removed and re-added (same display name), upload-then-delete
                // would delete the blob just uploaded.
                if (this.filesToRemove.length > 0) {
                    for (const fileName of this.filesToRemove) {
                        try {
                            await deleteFile(fileName, path)
                        } catch (error) {
                            console.error(
                                `Error deleting file ${fileName}:`,
                                error
                            )
                        }
                    }
                }

                if (this.pendingFilesToUpload.length > 0) {
                    for (const file of this.pendingFilesToUpload) {
                        try {
                            await saveFile(file, path, file.name)
                        } catch (error) {
                            console.error(
                                `Error uploading file ${file.name}:`,
                                error
                            )
                        }
                    }
                }

                // Reset file tracking arrays and pending files
                this.pendingFilesToUpload = []
                this.filesToAdd = []
                this.filesToRemove = []

                if (this.attachmentsToRemove.length > 0) {
                    for (const fileName of this.attachmentsToRemove) {
                        try {
                            await deleteFile(fileName, attachmentsPath)
                        } catch (error) {
                            console.error(
                                `Error deleting attachment ${fileName}:`,
                                error
                            )
                        }
                    }
                }

                if (this.pendingAttachmentsToUpload.length > 0) {
                    for (const file of this.pendingAttachmentsToUpload) {
                        try {
                            await saveFile(file, attachmentsPath, file.name)
                        } catch (error) {
                            console.error(
                                `Error uploading attachment ${file.name}:`,
                                error
                            )
                        }
                    }
                }

                // Reset attachment tracking arrays
                this.pendingAttachmentsToUpload = []
                this.attachmentsToAdd = []
                this.attachmentsToRemove = []

                this.$emit('invoiceUpdated', {
                    documentId: this.purchase.id,
                    dueDate: this.localInvoice.dueDate || null,
                    invoiceDate: this.localInvoice.invoiceDate || null,
                    consecutive: this.localInvoice.consecutive || null,
                    creditNote: !!this.localInvoice.creditNote,
                    file: finalFiles,
                    invoiceAttachments: finalAttachments,
                })
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
